import {
  REQUEST_WORKFLOW_ACTOR_TYPE,
  REQUEST_WORKFLOW_EVENT_TYPE,
  RequestWorkflowEventExternal,
} from '@workflows/types';
import {
  WorkflowLogActor,
  WorkflowLogEntry,
  WorkflowLogEntryInfo,
  WorkflowLogField,
  WorkflowLogFile,
} from '@workflows/types';
import {
  WorkflowLogFilterParams,
  WorkflowLogMapperLabels,
  WorkflowLogRow,
  WorkflowLogRowLabels,
  WorkflowLogStats,
  WorkflowLogTone,
  WorkflowLogToneStyle,
} from './request-workflow-events-dialog.types';

const MOSCOW_TIMEZONE = 'Europe/Moscow';

const TONE_STYLE_MAP: Record<WorkflowLogTone, WorkflowLogToneStyle> = {
  success: {
    dotColor: 'var(--tui-status-positive)',
    badgeBackgroundColor: 'var(--tui-status-positive-pale)',
    badgeTextColor: 'var(--tui-status-positive)',
  },
  warn: {
    dotColor: 'var(--tui-status-warning)',
    badgeBackgroundColor: 'var(--tui-status-warning-pale)',
    badgeTextColor: 'var(--tui-status-warning)',
  },
  danger: {
    dotColor: 'var(--tui-status-negative)',
    badgeBackgroundColor: 'var(--tui-status-negative-pale)',
    badgeTextColor: 'var(--tui-status-negative)',
  },
  neutral: {
    dotColor: 'var(--tui-border-hover)',
    badgeBackgroundColor: 'var(--tui-background-neutral-1-hover)',
    badgeTextColor: 'var(--tui-text-secondary)',
  },
};

export function normalizeEvents(
  events: RequestWorkflowEventExternal[],
  labels: WorkflowLogMapperLabels,
): WorkflowLogEntry[] {
  return events.map(event => {
    const entry: WorkflowLogEntry = {
      id: String(event.id),
      workflowId: String(event.requestWorkflowId),
      eventType: event.eventType,
      createdAt: toValidatedIsoString(event.createdAt),
      actor: mapActor(event, labels),
    };

    switch (event.eventType) {
      case REQUEST_WORKFLOW_EVENT_TYPE.REQUEST_WORKFLOW_SUBMISSION_SYNCED: {
        entry.formId = String(event.meta.formId);
        entry.formTitle = event.meta.formTitle;
        entry.submissionId = String(event.meta.submissionId);
        entry.isNewSubmission = event.meta.isNewSubmission;
        entry.isSubmissionValid = event.meta.isSubmissionValid;

        const fields = mapSyncedFields(event.meta.syncedSubmissionData);

        if (fields.length) {
          entry.fields = fields;
        }

        break;
      }

      case REQUEST_WORKFLOW_EVENT_TYPE.FILE_UPLOADED_TO_SUBMISSION:
      case REQUEST_WORKFLOW_EVENT_TYPE.FILE_DELETED_FROM_SUBMISSION: {
        entry.formId = String(event.meta.formId);
        entry.formTitle = event.meta.formTitle;
        entry.submissionId = String(event.meta.submissionId);

        const files = mapFileMeta(event.meta);

        if (files.length) {
          entry.files = files;
        }

        break;
      }

      default:
        break;
    }

    return entry;
  });
}

export function groupFileUploads(
  sortedEntries: WorkflowLogEntry[],
  windowMs = 15000,
): WorkflowLogEntry[] {
  const result: WorkflowLogEntry[] = [];

  for (const entry of sortedEntries) {
    const last = result[result.length - 1];
    const isUpload =
      entry.eventType ===
      REQUEST_WORKFLOW_EVENT_TYPE.FILE_UPLOADED_TO_SUBMISSION;

    const canGroup = Boolean(
      last &&
      last.eventType === 'fileUploadGroup' &&
      isUpload &&
      last.workflowId === entry.workflowId &&
      last.formId === entry.formId &&
      last.submissionId === entry.submissionId &&
      Math.abs(toTimestamp(last.createdAt) - toTimestamp(entry.createdAt)) <=
        windowMs,
    );

    if (canGroup && last) {
      last.ids = [...(last.ids ?? [last.id]), entry.id];
      last.files = [...(last.files ?? []), ...(entry.files ?? [])];
      continue;
    }

    if (isUpload) {
      result.push({
        ...entry,
        id: `upload-${entry.id}`,
        ids: [entry.id],
        eventType: 'fileUploadGroup',
        files: [...(entry.files ?? [])],
      });
      continue;
    }

    result.push(entry);
  }

  return result;
}

export function getEntryInfo(
  entry: WorkflowLogEntry,
  labels: WorkflowLogMapperLabels,
): WorkflowLogEntryInfo {
  const defaults: WorkflowLogEntryInfo = {
    label: labels.defaultEventLabel || entry.eventType,
    status: labels.defaultEventStatus,
    tone: 'neutral',
    bucket: 'system',
  };

  switch (entry.eventType) {
    case REQUEST_WORKFLOW_EVENT_TYPE.REQUEST_WORKFLOW_CREATED:
      return {
        label: labels.workflowCreatedLabel,
        status: labels.workflowCreatedStatus,
        tone: 'neutral',
        bucket: 'system',
      };

    case REQUEST_WORKFLOW_EVENT_TYPE.REQUEST_WORKFLOW_IN_PROGRESS:
      return {
        label: labels.workflowInProgressLabel,
        status: labels.workflowInProgressStatus,
        tone: 'warn',
        bucket: 'system',
      };

    case REQUEST_WORKFLOW_EVENT_TYPE.REQUEST_WORKFLOW_SUBMITTED:
      return {
        label: labels.workflowSubmittedLabel,
        status: labels.workflowSubmittedStatus,
        tone: 'success',
        bucket: 'system',
      };

    case REQUEST_WORKFLOW_EVENT_TYPE.REQUEST_WORKFLOW_SUBMISSION_SYNCED:
      if (entry.isSubmissionValid === false) {
        return {
          label: labels.submissionInvalidLabel,
          status: labels.submissionInvalidStatus,
          tone: 'danger',
          bucket: 'errors',
        };
      }

      if (
        entry.isSubmissionValid === true &&
        !entry.fields?.length &&
        entry.isNewSubmission === false
      ) {
        return {
          label: labels.submissionSyncedLabel,
          status: labels.submissionNoChangesStatus,
          tone: 'neutral',
          bucket: 'forms',
          quiet: true,
        };
      }

      return {
        label: labels.submissionSyncedLabel,
        status: labels.submissionValidStatus,
        tone: 'success',
        bucket: 'forms',
      };

    case REQUEST_WORKFLOW_EVENT_TYPE.FILE_UPLOADED_TO_SUBMISSION:
      return {
        label: labels.fileUploadedLabel,
        status: labels.fileUploadedStatus,
        tone: 'success',
        bucket: 'files',
      };

    case REQUEST_WORKFLOW_EVENT_TYPE.FILE_DELETED_FROM_SUBMISSION:
      return {
        label: labels.fileDeletedLabel,
        status: labels.fileDeletedStatus,
        tone: 'warn',
        bucket: 'files',
      };

    case 'fileUploadGroup':
      return {
        label: labels.fileUploadGroupLabel,
        status: labels.fileUploadedStatus,
        tone: 'success',
        bucket: 'files',
      };

    default:
      return defaults;
  }
}

export function buildSearchText(
  entry: WorkflowLogEntry,
  labels: WorkflowLogMapperLabels,
  workflowTitle?: string,
): string {
  const info = getEntryInfo(entry, labels);

  return [
    entry.id,
    ...(entry.ids ?? []),
    entry.workflowId,
    workflowTitle,
    entry.eventType,
    info.label,
    entry.formTitle,
    entry.formId,
    entry.submissionId,
    entry.actor.name,
    entry.actor.email,
    toMoscowSearchDate(entry.createdAt),
    entry.device?.kind,
    entry.device?.mode,
    entry.device?.os,
    entry.device?.browser,
    ...(entry.fields ?? []).flatMap(field => [
      field.key,
      field.label,
      String(field.value ?? ''),
    ]),
    ...(entry.files ?? []).flatMap(file => [
      file.name,
      file.category,
      file.fieldKey,
      file.requestFileId,
      file.submissionFileId,
    ]),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

export function buildWorkflowLogRows(
  entries: WorkflowLogEntry[],
  mapperLabels: WorkflowLogMapperLabels,
  labels: WorkflowLogRowLabels,
  getWorkflowLabel: (workflowId: string) => string,
): WorkflowLogRow[] {
  return entries.map(entry => {
    const workflowLabel = getWorkflowLabel(entry.workflowId);
    const info = getEntryInfo(entry, mapperLabels);

    return {
      entry,
      info,
      toneStyle: TONE_STYLE_MAP[info.tone],
      entityTitle:
        entry.formTitle || workflowLabel || labels.defaultWorkflowLabel,
      entitySubtitle: entry.submissionId
        ? `submission:${entry.submissionId}`
        : entry.formId
          ? `form:${entry.formId}`
          : entry.workflowId,
      actionSubtitle: isFileEvent(entry)
        ? `${labels.filesLabel}: ${entry.files?.length ?? 0}`
        : entry.formTitle || `${labels.eventIdLabel}: ${entry.id}`,
      statusText: info.status,
      searchText: buildSearchText(entry, mapperLabels, workflowLabel),
    } satisfies WorkflowLogRow;
  });
}

export function filterWorkflowLogRows(
  rows: WorkflowLogRow[],
  params: WorkflowLogFilterParams,
): WorkflowLogRow[] {
  const normalizedQuery = params.query.trim().toLowerCase();

  return rows.filter(row => {
    if (
      params.workflowId !== null &&
      row.entry.workflowId !== params.workflowId
    ) {
      return false;
    }

    if (params.hideQuiet && row.info.quiet) {
      return false;
    }

    if (params.filter === 'errors' && row.info.tone !== 'danger') {
      return false;
    }

    if (params.filter === 'files' && row.info.bucket !== 'files') {
      return false;
    }

    if (
      params.filter === 'forms' &&
      row.info.bucket !== 'forms' &&
      row.info.bucket !== 'errors'
    ) {
      return false;
    }

    if (params.filter === 'system' && row.info.bucket !== 'system') {
      return false;
    }

    if (!normalizedQuery) {
      return true;
    }

    return row.searchText.includes(normalizedQuery);
  });
}

export function buildWorkflowLogStats(
  rows: WorkflowLogRow[],
): WorkflowLogStats {
  return rows.reduce<WorkflowLogStats>(
    (stats, row) => {
      if (row.info.tone === 'success') {
        stats.successCount += 1;
      }

      if (row.info.tone === 'danger') {
        stats.errorCount += 1;
      }

      if (row.info.bucket === 'files') {
        stats.fileCount += 1;
      }

      return stats;
    },
    { successCount: 0, errorCount: 0, fileCount: 0 },
  );
}

function isFileEvent(entry: WorkflowLogEntry): boolean {
  return (
    entry.eventType ===
      REQUEST_WORKFLOW_EVENT_TYPE.FILE_UPLOADED_TO_SUBMISSION ||
    entry.eventType ===
      REQUEST_WORKFLOW_EVENT_TYPE.FILE_DELETED_FROM_SUBMISSION ||
    entry.eventType === 'fileUploadGroup'
  );
}

function mapActor(
  event: RequestWorkflowEventExternal,
  labels: WorkflowLogMapperLabels,
): WorkflowLogActor {
  if (event.actorType === REQUEST_WORKFLOW_ACTOR_TYPE.SYSTEM) {
    return {
      id: 'system',
      type: 'system',
      name: labels.systemActorName,
      initials: labels.systemActorName.trim().charAt(0).toUpperCase() || 'S',
    };
  }

  const fullName =
    event.user.person.fioWithoutPatronymic ||
    event.user.person.fio ||
    event.user.fullname ||
    event.user.email;

  return {
    id: String(event.user.id),
    type: REQUEST_WORKFLOW_ACTOR_TYPE.USER,
    name: fullName,
    fullName: event.user.person.fio || event.user.fullname,
    email: event.user.email,
    initials: buildInitials(
      event.user.person.firstName,
      event.user.person.lastName,
      fullName,
    ),
    avatarUrl: event.user.avatar,
  };
}

function mapSyncedFields(data: unknown): WorkflowLogField[] {
  if (!isRecord(data)) {
    return [];
  }

  return Object.entries(data).map(([key, value]) => ({
    key,
    label: key,
    value: toFieldValue(value),
  }));
}

function mapFileMeta(meta: {
  fileName: string;
  fileCatName: string;
  formFieldKey: string;
  requestFileId: number;
  submissionFileId: number;
}): WorkflowLogFile[] {
  return [
    {
      id: String(meta.submissionFileId),
      name: meta.fileName,
      category: meta.fileCatName,
      fieldKey: meta.formFieldKey,
      requestFileId: String(meta.requestFileId),
      submissionFileId: String(meta.submissionFileId),
    },
  ];
}

function buildInitials(
  firstName: string | undefined,
  lastName: string | undefined,
  fallbackName: string,
): string {
  const first = firstName?.trim().charAt(0) ?? '';
  const last = lastName?.trim().charAt(0) ?? '';

  if (first || last) {
    return `${first}${last}`.toUpperCase();
  }

  return fallbackName
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('');
}

function toFieldValue(value: unknown): string | number | boolean | null {
  if (value === null || value === undefined) {
    return null;
  }

  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value;
  }

  if (value instanceof Date) {
    return toValidatedIsoString(value);
  }

  try {
    return JSON.stringify(value);
  } catch {
    return '[unserializable]';
  }
}

function toValidatedIsoString(value: Date | string): string {
  const timestamp = toTimestampOrNaN(value);

  if (Number.isNaN(timestamp)) {
    return new Date(0).toISOString();
  }

  return new Date(timestamp).toISOString();
}

export function toTimestamp(value: Date | string): number {
  const timestamp = toTimestampOrNaN(value);

  return Number.isNaN(timestamp) ? 0 : timestamp;
}

export function parseDate(value: Date | string): Date | null {
  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
}

function toTimestampOrNaN(value: Date | string): number {
  const timestamp = new Date(value).getTime();

  return Number.isNaN(timestamp) ? Number.NaN : timestamp;
}

function toMoscowSearchDate(value: string): string {
  const timestamp = toTimestampOrNaN(value);

  if (Number.isNaN(timestamp)) {
    return '';
  }

  // Формируем строку в UTC+3 (Europe/Moscow) для поиска по локальному времени проекта.
  const moscowDate = new Date(timestamp);

  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: MOSCOW_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(moscowDate);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
